import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../@services/toast.service';
import { copyToClipboard } from '../ag-grid-export';

// Copy a compact "source reference" identifier to the clipboard — used on
// every /admin/common-data source panel so Mike can copy a token he can
// paste back to Claude for context ("look at <source>").
//
// The token itself is deliberately terse and unambiguous: raw Redis key
// (LSEG:INSTRUMENT_DETAILS), scoped mongo path (mongo:F2-ADMIN.tickers_master),
// or GitHub-derived list name (github:common-symbol-lists/lists/spx.csv).
// Panels supply the `source` string; label + tooltip are optional overrides.
@Component({
  standalone: true,
  selector: 'app-copy-source-button',
  imports: [CommonModule],
  template: `
  <button type="button"
          class="dark-btn dark-btn-sm"
          [disabled]="!source"
          [title]="tooltip || ('Copy source reference: ' + source)"
          (click)="doCopy()">
    <i class="fa-solid" [class.fa-copy]="!copied" [class.fa-check]="copied"
       [class.text-emerald-400]="copied"></i>
    <span class="ml-1">{{ copied ? 'Copied' : label }}</span>
  </button>
  `,
})
export class CopySourceButtonComponent {
  // Terse identifier to be copied to the clipboard. Example values:
  //   'LSEG:INSTRUMENT_DETAILS'
  //   'LSEG:INDEX_CONSTITUENTS:SPX'
  //   'github:common-symbol-lists/lists/spx.csv'
  //   'mongo:F2-ADMIN.tickers_master'
  @Input() source: string = '';

  // Button label; defaults to 'Copy Source' but panels can override
  // ('Copy Key', 'Copy Path', etc.) if a more specific verb fits.
  @Input() label: string = 'Copy Source';

  // Optional tooltip override. If unset, the tooltip shows the full source.
  @Input() tooltip: string = '';

  copied = false;

  constructor(private toast: ToastService) {}

  async doCopy() {
    if (!this.source) return;
    const ok = await copyToClipboard(this.source);
    if (ok) {
      this.copied = true;
      // Auto-reset the icon after 2s so subsequent clicks look responsive.
      setTimeout(() => (this.copied = false), 2000);
      this.toast.success('Copied source', this.source);
    } else {
      this.toast.error('Copy failed', 'Clipboard API rejected the write (browser permissions?).');
    }
  }
}
